<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <span class="d-flex align-items-center"
        :class="$attrs.class"
        :style="$attrs.style"
  >
    <template v-if="styledCheckbox">
      <span
        :class="[checkboxCssClass, checkedCssClass, presenceCheckboxWrapperCssClass, { disabled: disabled }]"
        data-test="s-checkbox-pseudo-label"
      >
        <span v-if="isButtonCheckbox"
              aria-hidden="true"
              data-test="s-checkbox-pseudo-label-text-sr"
        >{{ labelText }}</span>
        <template v-if="isPresenceCheckbox">
          <sdui-icon data-test="s-checkbox-pseudo--user-check" class="sdui-icon icon-user-check icon-16" />
          <sdui-icon data-test="s-checkbox-pseudo--user-uncheck" class="sdui-icon icon-user-remove icon-16" />
        </template>
      </span>
    </template>
    <template v-else>
      <div
        v-if="isScreenReaderActive"
        class="d-flex align-items-center"
        data-test="s-checkbox-sr-wrapper"
        :class="[
          labelCssClass,
          labelCheckedCssClass,
          additionalContentCssClass,
          additionalContentCheckedCssClass,
          { 'pointer-none': disabled },
        ]"
      >
        <label
          :for="inputId"
          data-test="s-checkbox-label"
          class="mb-0"
          :class="{ disabled: disabled, 'sr-only': !displayLabelText && !isButtonCheckbox, 'mr-3': !isButtonCheckbox }"
          :tabindex="isAndroid || isIOS ? -1 : undefined"
          aria-hidden="true"
        >
          {{ labelText }}
        </label>

        <template v-if="isPresenceCheckbox">
          <span :class="presenceCheckboxWrapperCssClass"
                aria-hidden="true"
                :tabindex="isAndroid || isIOS ? -1 : undefined"
          >
            <sdui-icon data-test="s-checkbox-user-check" class="sdui-icon icon-user-check icon-16" />
            <sdui-icon data-test="s-checkbox-user-uncheck" class="sdui-icon icon-user-remove icon-16" />
          </span>
        </template>

        <template v-if="hasAdditionalContent">
          <!-- We wrap the slot-content in an aria-hidden span to make sure that only the prop "labelText" is read by screenreaders. -->
          <span aria-hidden="true" class="additional-content-wrapper w-100 d-flex justify-content-between align-items-center flex-column">
            <slot name="additional-content"></slot>
          </span>
        </template>

        <!-- NOTE: This slot is only needed in rare use-cases, when SR is active and the CheckBox has a aria-hidden label -->
        <slot name="additional-sr-content"></slot>

        <input
          v-bind="filteredAttrs"
          :id="inputId"
          :disabled="disabled"
          :name="name"
          :checked="isChecked ? 'checked' : undefined"
          type="checkbox"
          :aria-label="labelText"
          class="hide-input"
          :class="[inputCssClass, inputCheckedCssClass]"
          data-test="s-checkbox-input"
          data-cy="s-checkbox-input"
          :aria-hidden="disabled && hasAdditionalSRContent && isIOS ? 'true' : undefined"
          :tabindex="isAndroid || isIOS ? 0 : undefined"
          v-on="_listeners"
          @input="handleChange"
          @focusin="focusClassIsActive = true"
          @focusout="focusClassIsActive = false"
        />
      </div>

      <label
        v-else
        class="mb-0 d-flex align-items-center"
        :class="[{ disabled: disabled }, labelCssClass, labelCheckedCssClass, additionalContentCssClass, additionalContentCheckedCssClass]"
        data-test="s-checkbox-label"
        :tabindex="isAndroid ? -1 : undefined"
      >
        <span
          data-test="s-checkbox-label-text"
          aria-hidden="true"
          :tabindex="isAndroid || isIOS ? -1 : undefined"
          :class="[
            disabled ? 'label-text-disabled' : 'cursor-pointer',
            { 'mr-3 ': !isButtonCheckbox },
            { 'sr-only': !isButtonCheckbox && !displayLabelText },
          ]"
        >
          {{ labelText }}
        </span>

        <template v-if="isPresenceCheckbox">
          <span :class="presenceCheckboxWrapperCssClass" :tabindex="isAndroid || isIOS ? -1 : undefined">
            <sdui-icon data-test="s-checkbox-user-check" class="sdui-icon icon-user-check icon-16" />
            <sdui-icon data-test="s-checkbox-user-uncheck" class="sdui-icon icon-user-remove icon-16" />
          </span>
        </template>

        <template v-if="hasAdditionalContent">
          <!-- We wrap the slot-content in an aria-hidden span to make sure that only the prop "labelText" is read by screenreaders. -->
          <span aria-hidden="true" class="w-100 d-flex justify-content-between align-items-center flex-column">
            <slot name="additional-content"></slot>
          </span>
        </template>

        <input
          v-bind="filteredAttrs"
          :id="inputId"
          :disabled="disabled"
          :name="name"
          :checked="isChecked ? 'checked' : undefined"
          :aria-label="labelText"
          type="checkbox"
          class="hide-input"
          :class="[inputCssClass, inputCheckedCssClass]"
          data-test="s-checkbox-input"
          data-cy="s-checkbox-input"
          :tabindex="isAndroid || isIOS ? 0 : undefined"
          v-on="_listeners"
          @input="handleChange"
          @focusin="focusClassIsActive = true"
          @focusout="focusClassIsActive = false"
        />
      </label>
    </template>
  </span>
</template>

<script>
export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SCheckbox',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    checkboxType: {
      type: String,
      default: 'square',
      validator: function (value) {
        return ['btn', 'switch', 'square', 'round', 'presence'].includes(value)
      },
    },

    labelText: {
      type: String,
      default: '',
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    displayLabelText: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // Enforce the styled checkbox (no input + label) for cases when the comp is being used for example on SduiUserListItem
    forceStyledCheckbox: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    inputId: null,
    isChecked: false,
    isAndroid: false,
    isIOS: false,
    focusClassIsActive: false,
    isScreenReaderActive: false,
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    checkboxCssClass() {
      return `${this.checkboxType}-checkbox`
    },

    checkedCssClass() {
      if (!this.isChecked) return ''
      return `${this.checkboxCssClass}--checked`
    },

    inputCssClass() {
      if (this.isButtonCheckbox) return 'btn-checkbox-input'
      if (this.isPresenceCheckbox) return 'presence-checkbox-input'
      return this.checkboxCssClass
    },

    inputCheckedCssClass() {
      if (this.isButtonCheckbox || this.isPresenceCheckbox || !this.isChecked) return ''
      return this.checkedCssClass
    },

    labelCssClass() {
      if (!this.isButtonCheckbox && !this.isPresenceCheckbox) return ''
      return this.checkboxCssClass
    },

    labelCheckedCssClass() {
      if ((!this.isButtonCheckbox && !this.isPresenceCheckbox) || !this.isChecked) return ''
      return this.checkedCssClass
    },

    presenceCheckboxWrapperCssClass() {
      if (!this.isPresenceCheckbox) return ''
      return this.isChecked ? 'presence-checkbox-wrapper bg-success-light' : 'presence-checkbox-wrapper bg-secondary'
    },

    additionalContentCssClass() {
      if (!this.hasAdditionalContent) return
      if (this.focusClassIsActive) return 'additional-content additional-content--focus'
      return 'additional-content'
    },

    additionalContentCheckedCssClass() {
      if (!this.isChecked || !this.hasAdditionalContent) return ''
      if (this.hasAdditionalContent) return 'additional-content--checked'
      return ''
    },

    _listeners() {
      // we need to remove the input listener, otherwise we get side effects
      // eslint-disable-next-line
      const { input, ...listeners } = this.$listeners;
      return listeners
    },

    isButtonCheckbox() {
      return this.checkboxType === 'btn'
    },

    isPresenceCheckbox() {
      return this.checkboxType === 'presence'
    },

    styledCheckbox() {
      // If the parent (e.g. SduiListItem) has a @click-listener, it will most likely turn into a <b-button>.
      // We must not render the input + label element inside of it in this case to avoid invalid HTML which can't be
      // accessed by screen readers.

      // TODO VUE3: If the parent element is a bootstrap component, the call to "this.$parent?.$listeners" will
      // throw an error "INSTANCE_LISTENERS compat has been disabled". That is because $listeners do not exist in Vue 3.
      // Change this to $attrs.onClick when the compat mode is removed.
      try {
        return !!this.$parent?.$listeners?.click || this.forceStyledCheckbox
      } catch (e) {
        // Catch bootstrap error
        if (e?.message?.includes('INSTANCE_LISTENERS compat has been disabled')) return false
        throw e
      }
    },

    hasAdditionalContent() {
      return this.$hasSlotContent(this.$slots['additional-content'])
    },

    hasAdditionalSRContent() {
      return this.$hasSlotContent(this.$slots['additional-sr-content'])
    },
  },

  emits: ['update:modelValue'],

  methods: {
    handleChange(event, forceValue) {
      if (this.disabled) return
      // event?.target?.checked and forceValue can both be "false", we need to check this correctly
      // For example "event?.target?.checked || forceValue" would resolve to undefined if "event?.target?.checked" is false
      if (event?.target?.checked !== undefined) {
        this.isChecked = event?.target?.checked
      } else if (forceValue !== undefined) {
        this.isChecked = forceValue
      }
      this.$emit('update:modelValue', this.isChecked)
    },
  },

  watch: {
    modelValue: {
      handler: function (newValue) {
        this.isChecked = newValue
      },

      immediate: true,
    },

    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  async mounted() {
    if (import.meta.env.IS_STORYBOOK) return
    this.isIOS = this.$store.state.isIOS
    this.isAndroid = this.$store.state.isAndroid
  },

  created() {
    this.isChecked = this.modelValue
    this.inputId = this.$uuid()
  },
}
</script>

<style scoped lang="scss">
label {
  font-size: pxtorem(16);
}

// BUTTON CHECKBOX -------------------------------------------------------------
.btn-checkbox-input {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 24px;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.btn-checkbox {
  border-radius: 24px;
  color: $color-gray-300;
  border: $color-gray-300 1px solid;
  padding: 6px 12px;
  font-size: pxtorem(15);
  font-weight: $font-weight-normal;
  min-height: initial;

  &:hover {
    background-color: $color-primary-light;
    color: $color-primary;
    border-color: $color-primary;
  }

  label {
    font-size: pxtorem(15);
  }
}

.btn-checkbox--big {
  padding: 8px 12px;
  font-size: pxtorem(16);
}

// SWITCH CHECKBOX -------------------------------------------------------------
.switch-checkbox {
  background-color: $color-gray-300;
  width: 52px;
  height: 32px;
  border-radius: 50px;

  &::after {
    top: 2px;
    left: 2px;
    width: 28px;
    height: 28px;
    background-color: $color-white;
    border-radius: 100%;
    transition: 0.15s;
    box-shadow:
      0 4px 8px rgba($color-black-rgb, 0.15),
      0 0 2px rgba($color-black-rgb, 0.08);
  }
}

.switch-checkbox--checked::after {
  transform: translateX(20px);
}

// ROUND CHECKBOX -------------------------------------------------------------
.round-checkbox {
  width: 28px;
  height: 28px;
  border: 1px solid $color-gray-300;
  border-radius: 50%;

  &::after {
    top: 8px;
    left: 7px;
    width: 12px;
    height: 6px;
  }
}

// SQUARE CHECKBOX -------------------------------------------------------------
.square-checkbox {
  min-width: 28px;
  min-height: 28px;
  border: 1px solid $color-gray-300;
  border-radius: 0.3rem;

  &::after {
    top: 6px;
    left: 5px;
    width: 16px;
    height: 8px;
  }
}

// PRESENCE CHECKBOX -------------------------------------------------------------
.presence-checkbox-input {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 32px;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.presence-checkbox-wrapper {
  display: flex;
  padding: 4px;
  border-radius: 32px;
  width: 56px;
}

.presence-checkbox {
  display: flex;
  height: 32px;

  .icon {
    height: 24px;
    width: 24px;
    align-items: center;
    border-radius: 50%;
    justify-content: space-around;
    transition: 0.1s;
  }

  .icon-user-check {
    color: rgba($color-gray-300-rgb, 0.6);
  }

  .icon-user-remove {
    color: $color-secondary;
    background: $color-gray-300;
  }
}

.presence-checkbox--checked {
  .icon-user-check {
    color: $color-success-light;
    background: $color-success;
  }

  .icon-user-remove {
    color: $color-success-dark;
    background: unset;
  }
}

// ADDITIONAL CONTENT -------------------------------------------------------------
.additional-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid $color-gray-100;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  opacity: 0.65;
  height: 100%;
  justify-content: space-between;
}

.additional-content--checked {
  border-color: $color-primary;
  opacity: 1;
}

// CUSTOM HANDLING FOR FOCUS AND ADDITIONAL-CONTENT ---------------------------------------------
// remove focus frame from input
.keyboard-navigation .additional-content input:focus-visible {
  outline: none !important;
  outline-offset: unset;
}

// ... and put it on to the label instead
.keyboard-navigation .additional-content--focus {
  outline: 3px solid $color-primary !important;
  outline-offset: 2px;
  z-index: 1;
}

// SHARED STYLES -------------------------------------------------------------
.btn-checkbox,
.switch-checkbox,
.round-checkbox,
.square-checkbox,
.presence-checkbox {
  position: relative;
  cursor: pointer;
  margin: 0;
}

.btn-checkbox,
.switch-checkbox,
.round-checkbox,
.square-checkbox {
  display: block;
}

.switch-checkbox::after,
.round-checkbox::after,
.square-checkbox::after {
  content: '';
  position: absolute;
}

.round-checkbox::after,
.square-checkbox::after {
  border: 2px solid $color-white;
  border-top: none;
  border-right: none;
  opacity: 0;
  transform: rotate(-45deg);
}

.btn-checkbox,
.round-checkbox,
.square-checkbox {
  background-color: $color-bg-primary;
}

.round-checkbox--checked,
.square-checkbox--checked {
  background-color: $color-primary;
  border-color: $color-primary;

  &::after {
    opacity: 1;
  }
}

.btn-checkbox--checked,
.btn-checkbox--checked:hover,
.switch-checkbox--checked {
  color: $color-white;
  background-color: $color-primary;
  border-color: $color-primary;
}

// disabled state (possible button + input)
input[type='checkbox']:disabled,
.btn-checkbox.disabled,
.round-checkbox.disabled,
.square-checkbox.disabled,
.switch-checkbox.disabled,
.additional-content.disabled,
.presence-checkbox.disabled {
  user-select: none;
  pointer-events: none;
  opacity: 0.6;
}

input[type='checkbox']:disabled,
.btn-checkbox.disabled,
.round-checkbox.disabled,
.square-checkbox.disabled,
.additional-content.disabled,
.switch-checkbox.disabled {
  filter: grayscale(1);
}

.label-text-disabled {
  opacity: 0.65;
}

// HIDE INPUT STYLES SO WE CAN USE OUR OWN -------------------------------------------------------------
.hide-input {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;

  &:focus {
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}
</style>
